// The application shell.
//
// THE DAY IS ONE SCREEN AMONG SEVERAL NOW, not the whole application. The
// bottom tab bar and the sidebar it becomes on a wide screen are here
// (`shell/NavBar.tsx`), which screen is on is the address bar's answer
// (`shell/useShell.ts`), and the vocabulary of screens is one list
// (`shell/screens.ts`). Screens are built independently behind this; if each
// invented its own way of being mounted there would be incompatible answers,
// so this file is deliberately the only place a screen is chosen.
//
// THE SHAPE EVERY SCREEN TAKES is one `.screen` inside `.pane`, holding
// whatever that screen needs - a sticky bar, a scroll pane, a floating button,
// a header - and the shell marks THAT inert while a sheet is open rather than
// reaching inside it. So a screen written later is behind a sheet's scrim
// correctly by existing, for pointers and for the keyboard alike, and nothing it
// draws has to know a sheet can appear over it.
//
// Over any screen, when there is something to make or change, a SHEET. It is
// the only thing on this page that is modal, and the modality is real rather
// than drawn: the screen and the whole navigation are marked `inert` while one
// is open, so the browser keeps focus and the screen reader inside the dialog
// without a hand-written focus trap to get wrong.

import { useCallback, useEffect, useRef, useState } from 'react';
import { BoardScreen } from './board/BoardScreen.tsx';
import { useBoard } from './board/useBoard.ts';
import { DayScreen } from './day/DayScreen.tsx';
import type { DayChoice } from './day/DayScreen.tsx';
import { dayProgress, pct } from './day/day-model.ts';
import { Icon } from './day/Icon.tsx';
import { useDayScreen } from './day/useDayScreen.ts';
import { AddSheet } from './edit/AddSheet.tsx';
import { EditItemSheet, NewItemSheet } from './edit/ItemSheet.tsx';
import { EditScheduleSheet, NewScheduleSheet } from './edit/ScheduleSheet.tsx';
import { editableFromAccount, editableFromDay } from './edit/edit-model.ts';
import { useEditor } from './edit/useEditor.ts';
import { FirstRunScreen } from './firstrun/FirstRunScreen.tsx';
import { useFirstRun } from './firstrun/useFirstRun.ts';
import { dayAndMonth, dayAndShortMonth } from './iso-day.ts';
import { MonthScreen } from './month/MonthScreen.tsx';
import { monthOfDay } from './month/month-model.ts';
import { THIS_MONTH, useMonthScreen } from './month/useMonthScreen.ts';
import { EditReminderSheet, ReminderSheet } from './reminders/ReminderSheet.tsx';
import { RemindersScreen } from './reminders/RemindersScreen.tsx';
import { NotesScreen } from './notes/NotesScreen.tsx';
import { ProgressScreen } from './progress/ProgressScreen.tsx';
import { RealizeScreen } from './realize/RealizeScreen.tsx';
import { useRealize } from './realize/useRealize.ts';
import { useProgressScreen } from './progress/useProgressScreen.ts';
import { NavBar } from './shell/NavBar.tsx';
import { SettingsScreen } from './shell/SettingsScreen.tsx';
import { useShell } from './shell/useShell.ts';
import { SignedOut } from './signin/SignedOut.tsx';

/** The header has to be off screen before the sticky bar earns its place. */
const STICKY_AFTER_PX = 96;

function toggled(set: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

export function App() {
  const state = useDayScreen();
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [openItems, setOpenItems] = useState<ReadonlySet<string>>(() => new Set());
  const [scrolled, setScrolled] = useState(false);
  const [noteShareState, setNoteShareState] = useState<{
    revision: number;
    pending: ReadonlyMap<string, boolean>;
  }>(() => ({ revision: 0, pending: new Map() }));
  const [routineNoteRevision, setRoutineNoteRevision] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);

  const { choose, say, amendNotice, dismiss, refresh, signOut } = state;

  // A routine note is drawn on the day, the reminders list and Notes. One
  // callback owns both invalidations so a clear or its Undo cannot refresh two
  // surfaces and leave the third holding a stale copy.
  const onRoutineNoteChanged = useCallback(() => {
    refresh();
    setRoutineNoteRevision((revision) => revision + 1);
  }, [refresh]);

  const editor = useEditor({
    refresh,
    onRoutineNoteChanged,
    say,
    dismiss,
    signOut,
    // Anchored to the day being LOOKED AT: a thing added while Tomorrow is on
    // screen belongs to tomorrow, and a one-off is due on its anchor day and no
    // other. Null until a day is known, which is also while nothing can be
    // added, because the + button needs the day's `schedules` figures anyway.
    anchorDay: state.day?.day ?? null,
    pinReminder: state.pinReminder,
  });

  // Above the shell so the shell can be told about the sheet: the browser's own
  // Back takes an open sheet with it, because the screen it lands on is not the
  // one the sheet was opened over - and that screen would be held inert under a
  // dialog about somewhere else. External navigation says this through `ShellHost`.
  const shell = useShell({
    onExternalNavigation: (navigation) => {
      editor.close();
      if (navigation.kind === 'notification') state.activateToday();
    },
  });
  const { go, backTo } = shell;

  // The board holds a read of its own, for the same reason Settings does: no day
  // payload carries the account's reminders, which is what a card on it is. It
  // is mounted here whatever screen is on, rather than inside `BoardScreen`, so
  // a board read once survives a look at Today and back - and `active` is what
  // keeps that from costing a request nobody asked for: nothing is fetched until
  // the board is the screen that is on.
  const board = useBoard({
    active: shell.screen === 'board',
    say,
    dismiss,
    // AND IT TELLS THE SHELL, exactly as `useEditor` and the reminders list do.
    // A card is a reminder, so finishing, making or archiving one on the board
    // changes what the day's block draws and what the year counts - and the
    // board was the one hook here that was given the toast and the gate but no
    // way to say a write had happened, so a card dragged into Complete stayed
    // open in the reminders block until the page was reloaded (captain,
    // 2026-08-19). See `BoardHost.refresh`.
    refresh,
    onSignedOut: signOut,
  });

  const onChoose = useCallback(
    (choice: DayChoice) => {
      choose(choice);
      // Landing part-way down yesterday because today was scrolled is
      // disorienting; every day starts at its own top.
      if (scroller.current !== null) scroller.current.scrollTop = 0;
      setScrolled(false);
    },
    [choose],
  );

  const { open } = editor;

  const onAdd = useCallback(() => {
    if (state.day === null) return;
    // TO THE DAY FIRST. The sidebar's add action can be pressed from Progress
    // or from Notes, and a thing made while one of those is on screen lands
    // somewhere the person cannot see - which is exactly the trap this client
    // has met four times (web/src/README.md): a write whose result is invisible
    // reads as a failure and is done again, and now there are two. The day is
    // where a new thing either appears or is told it does not, so that is where
    // the flow starts. On a phone the + is only ever on the day, so this is a
    // no-op there.
    go('today');
    // ALWAYS THE CHOOSER, even on an account with no schedules. It used to go
    // straight to the new-schedule flow there, because a thing to do had
    // nowhere to go - but a REMINDER belongs to no schedule, so that is no
    // longer the only answer to "add something", and skipping the question
    // would deny an empty account the one thing it can still write down. The
    // chooser leaves out the row that leads nowhere instead (`AddSheet`).
    open({ kind: 'add' });
  }, [go, open, state.day]);

  const onEditSection = useCallback(
    (sectionId: string) => {
      const section = state.day?.sections.find((s) => s.id === sectionId);
      if (section !== undefined) open({ kind: 'schedule', schedule: editableFromDay(section) });
    },
    [open, state.day],
  );

  const onEditItem = useCallback(
    (itemId: string, meta: string) => {
      open({ kind: 'item', itemId, meta });
    },
    [open],
  );

  const onSettings = useCallback(() => {
    go('settings');
  }, [go]);

  const onNoteShareChanged = useCallback((noteId: string, shared: boolean) => {
    setNoteShareState((current) => {
      const pending = new Map(current.pending);
      pending.set(noteId, shared);
      return { revision: current.revision + 1, pending };
    });
  }, []);

  const onNoteShareStatusRead = useCallback(
    (revision: number, observed: ReadonlyMap<string, boolean>) => {
      setNoteShareState((current) => {
        if (current.revision !== revision || current.pending.size === 0) return current;
        const pending = new Map(current.pending);
        let changed = false;
        for (const [noteId, shared] of observed) {
          if (pending.get(noteId) !== shared) continue;
          pending.delete(noteId);
          changed = true;
        }
        return changed ? { revision: current.revision, pending } : current;
      });
    },
    [],
  );

  // The month holds a read of its own - `GET /months/:month`, which no day
  // payload carries - so it watches the same `revision` Settings does, and any
  // 401 from it raises the same gate a 401 from the day does.
  //
  // AND IT WATCHES TICKS TOO, which Settings does not: a square's figure IS a
  // count of completions, so the commonest write in the product is the one that
  // makes the month disagree with the day it was made from. `completions` is a
  // second counter rather than `revision` because `revision` re-reads the day
  // screen as well, and re-reading the day on every tick would undo the
  // optimistic update that makes ticking feel instant.
  const month = useMonthScreen({
    active: shell.screen === 'month',
    revision: state.revision,
    completions: state.completions,
    onSignedOut: signOut,
  });

  // The year holds a read of its own - `GET /years/:year`, which no other
  // payload carries - and it is mounted here rather than inside `ProgressScreen`
  // for the reason the board and the month are: a year read once survives a look
  // at Today and back, and `active` is what keeps that from costing a request
  // nobody asked for.
  //
  // IT WATCHES TICKS, and it is the screen that watches them hardest: every
  // figure on it is a count of completion rows, so the commonest write in the
  // product is the one that makes it wrong. `completions` is a second counter
  // rather than `revision` for the same reason the month uses one - `revision`
  // re-reads the day screen as well, and re-reading the day on every tick would
  // undo the optimistic update that makes ticking feel instant.
  const progressScreen = useProgressScreen({
    active: shell.screen === 'progress',
    revision: state.revision,
    completions: state.completions,
    onSignedOut: signOut,
  });

  // Realize holds two reads of its own - the account's maps, and the map that
  // is open - which no other payload carries, so it is mounted here with the
  // board, the month and the year for the same two reasons: a map read once
  // survives a look at Today and back, and `active` keeps it from costing a
  // request nobody asked for.
  //
  // It watches NOTHING. Every other screen mounted here follows `revision` or
  // `completions`, because each draws the same schedules and completions the day
  // does. A mind map is not on anybody's day: nothing ticked, archived or
  // scheduled can change what is on one, so a counter here would be a re-read
  // per tick of data no tick can touch.
  const realize = useRealize({
    active: shell.screen === 'realize',
    say,
    dismiss,
    signOut,
  });

  const { go: goToMonth } = month;

  // THE MONTH AROUND THE DAY BEING LOOKED AT. Month is NOT a tab (the approved
  // navigation is four, and screens claiming one by default would assemble a bar
  // nobody chose - `shell/screens.ts`), so the control in the day screen's date
  // header is the only way to it - and it opens the month the day ON SCREEN
  // is in rather than wherever the month screen was left. Tomorrow on 31 August
  // opens September, and pressing it again after browsing to July comes back to
  // the day's own month instead of to July.
  //
  // `state.dayKey` is the day the screen NAMES, which is deliberately a
  // different question from the day it ASKS for (`useDayScreen`'s `Target`):
  // heading the month with September while the date above said 31 August would
  // be the same silent substitution that `Target` exists to prevent, one screen
  // along. Until a day is named there is nothing to take a month off, and the
  // literal goes to the server to be resolved in the user's own stored zone - no
  // client here works out which month it is from a clock.
  const onMonth = useCallback(() => {
    goToMonth(state.dayKey === null ? THIS_MONTH : monthOfDay(state.dayKey));
    go('month');
  }, [goToMonth, go, state.dayKey]);

  // The month can open the three days the day screen is able to show, and only
  // those (`choiceFor` in month/month-model.ts). The day is CHOSEN before the
  // screen is changed, so Today is already showing the right one when it
  // arrives rather than showing today for a frame and then moving.
  const onOpenDay = useCallback(
    (choice: DayChoice) => {
      onChoose(choice);
      go('today');
    },
    [onChoose, go],
  );

  const onOpenReminders = useCallback(() => {
    go('reminders');
  }, [go]);

  const onRealize = useCallback(() => {
    go('realize');
  }, [go]);

  const onBackFromRealize = useCallback(() => {
    backTo('notes');
  }, [backTo]);

  const onEditReminder = useCallback(
    (reminderId: string) => {
      open({ kind: 'reminder', reminderId });
    },
    [open],
  );

  // The + on the reminders screen, and the "A reminder" row in the add sheet.
  // `from` decides one sentence in the toast and nothing else - see the note on
  // `new-reminder` in `edit/useEditor.ts`.
  const onNewReminder = useCallback(
    (from: 'day' | 'reminders') => {
      open({ kind: 'new-reminder', from });
    },
    [open],
  );

  // A screen that has just been mounted is at its own top, whatever the last one
  // was scrolled to - and the sticky bar is drawn from this flag rather than
  // from a measurement, so it has to be told. Without it, leaving a scrolled day
  // for Notes and coming back puts the mini bar over the day's own header with
  // the page at the top: a bar reporting a header that is right there.
  useEffect(() => {
    setScrolled(false);
  }, [shell.screen]);

  // The first-run gate's own read. Mounted here with the other hooks rather than
  // inside the gate, because hooks may not be called conditionally and the gate
  // is behind an early return - and `active` is what keeps it from costing a
  // request nobody asked for: nothing is fetched unless the gate is up.
  const firstRun = useFirstRun({
    active: state.firstRun,
    // The DAY is what lowers the gate, once its payload says the choice is
    // settled - and that payload is the one carrying whatever was just written.
    // See `FirstRunHost.onSettled`.
    onSettled: refresh,
    onSignedOut: signOut,
  });

  const progress = state.day === null ? null : dayProgress(state.day.sections);

  // Below every hook, and above everything else: whether there is a session is
  // the first question this shell answers, because it decides which screen there
  // is rather than what one of them says.
  //
  // It replaces the whole shell, the navigation with it, rather than one
  // screen's body. Every control the shell draws acts on data a signed-out
  // visitor does not have and cannot get, so leaving them on screen around a
  // sign-in prompt would be a page of things that all fail. Whatever a tick had
  // painted goes with it, which is correct: the write it was showing did not
  // land, and the row is already back to what the server holds.
  if (state.signedOut) return <SignedOut />;

  // The second question, and it is asked in this order because a 401 outranks
  // everything: somebody with no session has no account to be on the first run
  // of. An account that has never made its first-run choice has no content on
  // any day, so the shell behind this gate would be a day screen saying "Nothing
  // scheduled" to somebody who has just signed up - which is exactly the empty
  // canvas the first-run content exists to prevent.
  //
  // It replaces the whole shell, the navigation with it, for the reason the
  // signed-out gate does: every control the shell draws acts on schedules this
  // account has not decided whether to have.
  if (state.firstRun) {
    return (
      // The DAY's failure and the DAY's retry, handed in rather than fetched
      // again: while this gate is up the only day read that can fail is the one
      // the choice asks for, and it is also the one that takes the gate down. A
      // gate with no way to say that is an answered account looking at two dead
      // buttons - see `FirstRunScreenProps.dayProblem`.
      <FirstRunScreen state={firstRun} dayProblem={state.failure} onRetryDay={state.retry} />
    );
  }

  const sheet = editor.sheet;
  // Exactly the condition the sheet is rendered under, and deliberately not
  // `sheet !== null`: a page made inert with no dialog over it is a page with
  // nothing on it that can be used at all.
  //
  // Every sheet but the schedule editor and the reminder sheets is about the
  // day and needs its figures. The schedule editor is opened from the list in
  // Settings as well, where there is no day behind it at all - which is the
  // whole reason a schedule the day is not drawing is reachable now - and a
  // reminder belongs to no day by definition: it is written down today whatever
  // is on screen, so the reminders list can create or edit one with no day
  // loaded at all.
  const covered =
    sheet !== null &&
    (sheet.kind === 'schedule' ||
      sheet.kind === 'new-reminder' ||
      sheet.kind === 'reminder' ||
      state.day !== null);
  // The day a one-off added right now would fall on, in the words the page
  // heads itself with, so the sheet and the date agree.
  const onceOn = state.day === null ? '' : dayAndMonth(state.day.day);

  return (
    <div className="app">
      <div className={scrolled ? 'pane scrolled' : 'pane'}>
        <div className="screen" inert={covered}>
          {shell.screen === 'today' && (
            <>
              {state.day !== null && progress !== null && (
                <div className="minibar" aria-hidden="true">
                  <b className="tnum">
                    {dayAndShortMonth(state.day.day)} - {progress.done}/{progress.total}
                  </b>
                  {/* The counts belong on every day; the bar does not. The
                      prototype states this rule for itself - its Tomorrow
                      caption reads "A future day has no progress bar. Nothing
                      is due yet.", and its CSS says "A future day is a PLAN.
                      The distinction from a past day is carried by ONE channel
                      - the bar" - and its own mini bar is the one place it does
                      not honour it. Honouring it here completes the approved
                      design rather than reinterpreting it. */}
                  {!state.future && (
                    <div className="bar">
                      <i style={{ width: `${pct(progress)}%` }} />
                    </div>
                  )}
                </div>
              )}

              <div
                className="scroll hasfab"
                ref={scroller}
                onScroll={(event) => setScrolled(event.currentTarget.scrollTop > STICKY_AFTER_PX)}
              >
                <DayScreen
                  day={state.day}
                  dayKey={state.dayKey}
                  ready={state.ready}
                  loading={state.loading}
                  failure={state.failure}
                  onRetry={state.retry}
                  future={state.future}
                  choice={state.choice}
                  onChoose={onChoose}
                  folded={folded}
                  openItems={openItems}
                  onToggleFold={(id) => setFolded((set) => toggled(set, id))}
                  onToggleItem={state.toggleItem}
                  onToggleStep={state.toggleStep}
                  onToggleOpen={(id) => setOpenItems((set) => toggled(set, id))}
                  onToggleReminder={state.toggleReminder}
                  pinnedReminders={state.pinnedReminders}
                  onOpenReminders={onOpenReminders}
                  onEditReminder={onEditReminder}
                  onEditSection={onEditSection}
                  onEditItem={onEditItem}
                  onAdd={onAdd}
                  onSettings={onSettings}
                  onMonth={onMonth}
                />
              </div>

              {/* The phone's add action. Disabled rather than hidden while a
                  day is on its way: the clearance under the last row exists for
                  it, and a button that comes and goes as the day loads is a
                  moving target. It is hidden at sidebar width, where the
                  sidebar's own "Add something" replaces it - a corner-anchored
                  circle on a 1440px window is a phone idiom in the wrong
                  place. */}
              <button
                className="fab"
                aria-label="Add something"
                disabled={state.day === null}
                onClick={onAdd}
              >
                <Icon name="plus" />
              </button>
            </>
          )}

          {/* A tab of its own (captain, 2026-08-18), so it takes the account
              button every tab carries rather than a Back control - and still no
              floating +, because the board's add action is at the foot of each
              column, where the card it makes lands. */}
          {shell.screen === 'board' && <BoardScreen {...board} onSettings={onSettings} />}

          {shell.screen === 'month' && (
            <MonthScreen state={month} onBack={shell.back} onOpenDay={onOpenDay} />
          )}

          {shell.screen === 'notes' && (
            <NotesScreen
              say={say}
              amendNotice={amendNotice}
              dismiss={dismiss}
              shareRevision={noteShareState.revision}
              pendingShareStatus={noteShareState.pending}
              onShareStatusRead={onNoteShareStatusRead}
              onShareChanged={onNoteShareChanged}
              routineNoteRevision={routineNoteRevision}
              onRoutineNoteChanged={onRoutineNoteChanged}
              onSignedOut={signOut}
              onSettings={onSettings}
              onOpenRealize={onRealize}
            />
          )}

          {/* A page INSIDE the Notes tab (captain, 2026-08-19), so it takes a
              header with its way out rather than a tab of its own - the shape
              Month and Reminders take off the day. Its Back goes to Notes; the
              Back inside a map goes to the list of maps first. */}
          {shell.screen === 'realize' && (
            <RealizeScreen state={realize} onBack={onBackFromRealize} />
          )}

          {/* The last placeholder in this shell became a screen here. Nothing
              mounts `shell/Placeholder.tsx` any more and both it and `UNBUILT`
              in `screens.ts` are deliberately kept: the next screen added ahead
              of its own workstream is one entry and one mount away from an
              honest tab, which is the whole point of that pair. Its import went
              because an unused one is a lint error, not because it was
              retired. */}
          {shell.screen === 'progress' && (
            <ProgressScreen
              state={progressScreen}
              onSettings={onSettings}
              onOpenReminders={onOpenReminders}
            />
          )}

          {shell.screen === 'reminders' && (
            <RemindersScreen
              revision={state.revision}
              today={state.today}
              onBack={shell.back}
              onNew={() => onNewReminder('reminders')}
              onEdit={onEditReminder}
              onRemove={(id, title) => void editor.removeReminder(id, title)}
              onSignedOut={signOut}
              say={say}
              refresh={refresh}
            />
          )}

          {shell.screen === 'settings' && (
            <SettingsScreen
              revision={state.revision}
              schedules={state.day?.schedules ?? null}
              onBack={shell.back}
              onEditSchedule={(section) =>
                open({ kind: 'schedule', schedule: editableFromAccount(section) })
              }
              onRestore={(section) => editor.restoreSchedule(section.id, section.name)}
              onSignedOut={signOut}
              say={say}
            />
          )}
        </div>

        {/* Mounted whether or not there is anything to say. A live region that
            arrives with its text already in it is routinely not announced, and
            "Not saved" is the one line that has to be heard.

            Inside the pane and OUTSIDE the screen, which is the pair that makes
            it work: Undo is the way back from archiving something, archiving is
            done FROM a sheet, so the toast has to stay reachable in the moment
            the sheet closes - and it must not belong to whichever screen
            happens to be mounted, because a line about what was just written
            outlives a change of screen. Positioning against the pane rather
            than against the whole application is what keeps it clear of the tab
            bar on a phone and centred on the reading column on a desktop - the
            sheets go the other way, and the reason is at them.

            `under` is what makes "underneath a sheet" true rather than said.
            The toast outranks everything on a screen, which is right, and
            outranked the sheet too - so it floated over an open one and
            swallowed the taps in its own band. While a sheet is up it drops
            below the sheet and its scrim, where the modality puts it; it comes
            straight back the moment the sheet closes, still inside its window.

            And `inert` with it, for the same span, because a z-index says
            nothing to a keyboard: without it the Undo stayed in the tab order
            while everything else behind the scrim was out of it, so the first
            Tab out of the dialog landed on a button drawn behind the scrim and
            Enter undid something with nothing on screen to say so. Modality is
            one decision, not one for pointers and another for everybody
            else. */}
        <div
          className={['toast', state.notice === null ? '' : 'on', covered ? 'under' : '']
            .filter(Boolean)
            .join(' ')}
          role="status"
          inert={covered}
        >
          <span>{state.notice?.message}</span>
          {state.notice?.undo !== undefined && <button onClick={state.notice.undo}>Undo</button>}
        </div>
      </div>

      <NavBar
        screen={shell.screen}
        onGo={go}
        onAdd={onAdd}
        canAdd={state.day !== null}
        inert={covered}
      />

      {/* OUTSIDE THE PANE, beside the navigation rather than inside the screen.
          A sheet's scrim is positioned against `.app`, so it covers the tab bar
          and the sidebar as well as the screen - which is where a modal
          dialog's modality puts them, and what `inert` on the navigation
          already says to a keyboard. Inside the pane the scrim stopped at the
          sidebar's edge, and a column of controls drawn at full strength beside
          an open dialog is a column of controls people press. */}
      {covered && sheet !== null && (
        <>
          {/* Opened from a card on the day OR from the schedules list in
              Settings, which is why it sits outside the `state.day` block
              below: that list is the only way to reach a schedule the day is
              not drawing, and gating this on a day would put the door back
              where it was. */}
          {sheet.kind === 'schedule' && (
            <EditScheduleSheet
              schedule={sheet.schedule}
              busy={editor.busy}
              problem={editor.problem}
              onSave={(id, patch) => void editor.saveSchedule(id, patch)}
              onArchive={(id, name) => void editor.archiveSchedule(id, name)}
              onDeleteForever={(id, name) => void editor.deleteScheduleForever(id, name)}
              onClose={editor.close}
            />
          )}

          {/* Outside the `state.day` block for the same reason the schedule
              editor is: the reminders screen opens this one, and a reminder is
              anchored to the server's today rather than to the day on screen -
              so gating it on a day would be gating it on something it does not
              use. */}
          {sheet.kind === 'new-reminder' && (
            <ReminderSheet
              today={state.today}
              busy={editor.busy}
              problem={editor.problem}
              onCreate={(input) => void editor.createReminder(input, sheet.from)}
              onClose={editor.close}
            />
          )}

          {sheet.kind === 'reminder' && (
            <EditReminderSheet
              key={sheet.reminderId}
              reminderId={sheet.reminderId}
              today={state.today}
              busy={editor.busy}
              conflicted={editor.routineNoteConflict}
              problem={editor.problem}
              onSave={(id, patch) => void editor.saveReminder(id, patch)}
              onSignedOut={signOut}
              onClose={editor.close}
            />
          )}

          {state.day !== null && (
            <>
              {sheet.kind === 'add' && (
                <AddSheet
                  onNewReminder={() => onNewReminder('day')}
                  onNewItem={() => open({ kind: 'new-item', sectionId: null })}
                  onNewSchedule={() => open({ kind: 'new-schedule' })}
                  // The ACCOUNT's figure, not the cards on the day: a schedule
                  // with nothing due today is left out of the day and is still
                  // a place a new thing can go.
                  hasSchedules={state.day.schedules.total > 0}
                  onClose={editor.close}
                />
              )}

              {sheet.kind === 'new-schedule' && (
                <NewScheduleSheet
                  schedules={state.day.schedules}
                  onceOn={onceOn}
                  busy={editor.busy}
                  problem={editor.problem}
                  onCreate={(input) => void editor.createSchedule(input)}
                  // At the cap, the way to act on it is the schedules list - a
                  // place, not a list inside this sheet. See `AtCapSheet`.
                  onManageSchedules={() => {
                    editor.close();
                    go('settings');
                  }}
                  onClose={editor.close}
                />
              )}

              {sheet.kind === 'new-item' && (
                <NewItemSheet
                  sectionId={sheet.sectionId}
                  onceOn={onceOn}
                  busy={editor.busy}
                  problem={editor.problem}
                  onCreate={(input) => void editor.createItem(input)}
                  onNewSchedule={() => open({ kind: 'new-schedule' })}
                  onSignedOut={signOut}
                  onClose={editor.close}
                />
              )}

              {sheet.kind === 'item' && (
                <EditItemSheet
                  // Keyed by the row it is about, so opening a second item
                  // after a first replaces the form rather than reusing one
                  // holding the previous row's title in its own state.
                  key={sheet.itemId}
                  itemId={sheet.itemId}
                  meta={sheet.meta}
                  busy={editor.busy}
                  conflicted={editor.routineNoteConflict}
                  problem={editor.problem}
                  onSave={(id, patch) => void editor.saveItem(id, patch)}
                  onArchive={(id, title) => void editor.archiveItem(id, title)}
                  onDeleteForever={(id, title) => void editor.deleteItemForever(id, title)}
                  onSignedOut={signOut}
                  onClose={editor.close}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
